/**
 * BadgesTab Component
 * Badge styles preview and editing
 */

import { useState } from 'react';
import { TabProps } from '../types';
import { ColorInput } from '../ColorInput';

// Default badge sizes when not defined in scheme
const DEFAULT_BADGE_SIZES = {
  small: { paddingX: '0.5rem', paddingY: '0.125rem', fontSize: '0.75rem', borderRadius: '9999px' },
  medium: { paddingX: '0.75rem', paddingY: '0.25rem', fontSize: '0.875rem', borderRadius: '9999px' },
  large: { paddingX: '1rem', paddingY: '0.375rem', fontSize: '1rem', borderRadius: '9999px' },
};

export function BadgesTab({
  scheme,
  t,
  onColorChange,
  canEdit,
}: TabProps) {
  const [expandedBadge, setExpandedBadge] = useState<string | null>(null);
  const [expandedSize, setExpandedSize] = useState<string | null>(null);

  const badgeTypes = [
    { key: 'default' as const },
    { key: 'primary' as const },
    { key: 'secondary' as const },
    { key: 'success' as const },
    { key: 'warning' as const },
    { key: 'error' as const },
    { key: 'info' as const },
  ];

  // Get badge sizes from scheme or use defaults
  const badgeSizes = scheme.badges.sizes || DEFAULT_BADGE_SIZES;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">{t('sections.badgeStyles')}</h3>
        {canEdit && (
          <p className="text-sm text-[var(--color-text-muted)] italic cursor-default select-none">{t('hints.clickToEdit')}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {badgeTypes.map(({ key }) => {
          const style = scheme.badges[key];
          const label = t(`badgeTypes.${key}.label`);
          const isExpanded = expandedBadge === key;

          return (
            <div
              key={key}
              className={`rounded-lg border transition-all ${
                isExpanded
                  ? 'bg-[var(--color-background-card)] border-primary-300 shadow-lg'
                  : 'bg-[var(--color-background-surface)] border-transparent hover:border-[var(--color-border-default)]'
              }`}
            >
              <div
                className={`p-4 ${canEdit ? 'cursor-pointer' : ''}`}
                onClick={() => canEdit && setExpandedBadge(isExpanded ? null : key)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="min-h-[40px]">
                    <h4 className="font-medium text-[var(--color-text-primary)]">{label}</h4>
                    <p className="text-xs text-[var(--color-text-tertiary)]">{t(`badgeTypes.${key}.description`)}</p>
                  </div>
                  {canEdit && (
                    <svg
                      className={`w-5 h-5 text-[var(--color-text-tertiary)] transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>

                {/* Badge Preview */}
                <div className="flex items-center justify-center py-3">
                  <span
                    className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: style.background,
                      color: style.text,
                    }}
                  >
                    {label}
                  </span>
                </div>
              </div>

              {/* Expanded Color Editor */}
              {isExpanded && canEdit && (
                <div className="px-4 pb-4 border-t border-[var(--color-border-default)] pt-4 space-y-3">
                  <ColorInput
                    label={t('badgeProperties.background')}
                    path={`badges.${key}.background`}
                    value={style.background}
                    onColorChange={onColorChange}
                    canEdit={canEdit}
                  />
                  <ColorInput
                    label={t('badgeProperties.text')}
                    path={`badges.${key}.text`}
                    value={style.text}
                    onColorChange={onColorChange}
                    canEdit={canEdit}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Badge sizes */}
      <div className="pt-4 border-t border-[var(--color-border-default)]">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-[var(--color-text-primary)]">{t('sections.badgeSizes')}</h4>
          {canEdit && (
            <p className="text-sm text-[var(--color-text-muted)] italic cursor-default select-none">{t('hints.clickToEdit')}</p>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {(['small', 'medium', 'large'] as const).map((size) => {
            const sizeStyle = badgeSizes[size];
            const isExpanded = expandedSize === size;

            return (
              <div
                key={size}
                className={`rounded-lg border transition-all ${
                  isExpanded
                    ? 'bg-[var(--color-background-card)] border-primary-300 shadow-lg'
                    : 'bg-[var(--color-background-surface)] border-transparent hover:border-[var(--color-border-default)]'
                }`}
              >
                <div
                  className={`p-4 ${canEdit ? 'cursor-pointer' : ''}`}
                  onClick={() => canEdit && setExpandedSize(isExpanded ? null : size)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium text-[var(--color-text-primary)] capitalize">{t(`sizes.${size}`)}</h5>
                    {canEdit && (
                      <svg
                        className={`w-4 h-4 text-[var(--color-text-tertiary)] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>

                  {/* Badge Size Preview */}
                  <div className="flex items-center justify-center py-2">
                    <span
                      className="font-medium"
                      style={{
                        backgroundColor: scheme.badges.primary.background,
                        color: scheme.badges.primary.text,
                        paddingLeft: sizeStyle.paddingX,
                        paddingRight: sizeStyle.paddingX,
                        paddingTop: sizeStyle.paddingY,
                        paddingBottom: sizeStyle.paddingY,
                        fontSize: sizeStyle.fontSize,
                        borderRadius: sizeStyle.borderRadius,
                      }}
                    >
                      {t(`sizes.${size}`)}
                    </span>
                  </div>
                </div>

                {/* Expanded Size Editor */}
                {isExpanded && canEdit && (
                  <div className="px-4 pb-4 border-t border-[var(--color-border-default)] pt-4 space-y-3">
                    <div>
                      <label className="text-xs font-medium text-[var(--color-text-secondary)] block mb-1">
                        {t('badgeSizeProperties.paddingX')}
                      </label>
                      <input
                        type="text"
                        value={sizeStyle.paddingX}
                        onChange={(e) => onColorChange(`badges.sizes.${size}.paddingX`, e.target.value)}
                        className="w-full text-xs font-mono bg-[var(--color-background-input)] px-2 py-1.5 rounded border border-[var(--color-border-default)] text-[var(--color-text-primary)] focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                        placeholder="0.75rem"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[var(--color-text-secondary)] block mb-1">
                        {t('badgeSizeProperties.paddingY')}
                      </label>
                      <input
                        type="text"
                        value={sizeStyle.paddingY}
                        onChange={(e) => onColorChange(`badges.sizes.${size}.paddingY`, e.target.value)}
                        className="w-full text-xs font-mono bg-[var(--color-background-input)] px-2 py-1.5 rounded border border-[var(--color-border-default)] text-[var(--color-text-primary)] focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                        placeholder="0.25rem"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[var(--color-text-secondary)] block mb-1">
                        {t('badgeSizeProperties.fontSize')}
                      </label>
                      <input
                        type="text"
                        value={sizeStyle.fontSize}
                        onChange={(e) => onColorChange(`badges.sizes.${size}.fontSize`, e.target.value)}
                        className="w-full text-xs font-mono bg-[var(--color-background-input)] px-2 py-1.5 rounded border border-[var(--color-border-default)] text-[var(--color-text-primary)] focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                        placeholder="0.875rem"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[var(--color-text-secondary)] block mb-1">
                        {t('badgeSizeProperties.borderRadius')}
                      </label>
                      <input
                        type="text"
                        value={sizeStyle.borderRadius}
                        onChange={(e) => onColorChange(`badges.sizes.${size}.borderRadius`, e.target.value)}
                        className="w-full text-xs font-mono bg-[var(--color-background-input)] px-2 py-1.5 rounded border border-[var(--color-border-default)] text-[var(--color-text-primary)] focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
                        placeholder="9999px"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default BadgesTab;
